import { jwtDecode } from 'jwt-decode';

// Storage keys - centralized to avoid typos and enable easy changes
export const STORAGE_KEYS = {
    TOKEN: 'token',
    REFRESH_TOKEN: 'refresh_token',
    CODE_VERIFIER: 'code_verifier',
    REDIRECT_AFTER_LOGIN: 'redirect_after_login',
    USER_LOGGED_OUT: 'user_logged_out',
    AUTO_LOGIN_IN_PROGRESS: 'auto_login_in_progress',
    IDENTITY: 'identity', // sessionStorage
} as const;

/**
 * Custom-Event-Name fuer Token-Updates. Wird gefeuert, wenn setToken einen
 * neuen Token in den Storage schreibt — H5PEditor (und andere lang-laufende
 * Editor-Iframes) hoeren darauf, um ihren intern gepufferten Token zu
 * aktualisieren. Issue #295.
 */
export const TOKEN_UPDATED_EVENT = 'oa:token-updated';

// JWT payload interface
export interface JwtPayload {
    exp: number;
    iat?: number;
    sub?: string;
    email?: string;
    userId?: number;
    companyId?: number;
    /** Alle Mandanten der Unternehmensgruppe (inkl. eigenem), Claim aus dem SSO */
    companyGroupMemberIds?: number[];
    roles?: string[] | Record<string, string>;
    permissions?: string[] | Record<string, string>;
    features?: string[] | Record<string, string>;
}

// User identity interface
export interface UserIdentity {
    id: number;
    uuid: string;
    fullName: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
    companyId: number | null;
}

class TokenService {
    /**
     * In-flight refresh-promise — verhindert, dass mehrere parallele 401s
     * jeweils einen eigenen Refresh-Call ausloesen (Refresh-Token waere nach
     * dem ersten erfolgreichen Call schon rotiert und damit invalid).
     */
    private refreshPromise: Promise<boolean> | null = null;

    /**
     * Get the stored JWT token
     */
    getToken(): string | null {
        return localStorage.getItem(STORAGE_KEYS.TOKEN);
    }

    /**
     * Store a JWT token. Feuert ein `oa:token-updated`-Event, damit lange
     * laufende Editor-Iframes (H5P, #295) ihren intern gepufferten Token
     * aktualisieren koennen.
     */
    setToken(token: string): void {
        localStorage.setItem(STORAGE_KEYS.TOKEN, token);
        try {
            window.dispatchEvent(new CustomEvent(TOKEN_UPDATED_EVENT, { detail: { token } }));
        } catch {
            // Test-Env / kein window → silently ignore
        }
    }

    /**
     * Remove the stored token
     */
    removeToken(): void {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
    }

    /**
     * Get the stored refresh token
     */
    getRefreshToken(): string | null {
        return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    }

    /**
     * Store a refresh token. Refresh tokens werden bei jedem Refresh
     * rotiert — neuer Token muss gespeichert werden.
     */
    setRefreshToken(refreshToken: string): void {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }

    /**
     * Remove the stored refresh token
     */
    removeRefreshToken(): void {
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    }

    /**
     * Tauscht den Refresh-Token gegen einen neuen Access-Token (und einen
     * neuen Refresh-Token). Verwendet die OAuth2 `refresh_token`-Grant gegen
     * den SSO-Server. Concurrent-Calls werden auf eine einzige in-flight
     * Promise dedupliziert, damit der Refresh-Token nicht doppelt eingeloest
     * wird (waere nach dem ersten Call rotiert).
     *
     * Returns true bei Erfolg, false wenn kein Refresh-Token gespeichert ist
     * oder der Refresh-Call fehlschlaegt (z.B. expired refresh-token).
     * Tokens werden nur bei 4xx-Antworten entfernt (Refresh-Token verbraucht
     * oder invalid) — bei 5xx/Netzwerkfehlern bleiben sie erhalten, damit ein
     * transienter SSO-Ausfall keine laufende Session beendet.
     */
    refreshAccessToken(): Promise<boolean> {
        if (this.refreshPromise !== null) {
            return this.refreshPromise;
        }

        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            return Promise.resolve(false);
        }

        const ssoUrl = process.env.REACT_APP_SSO_URL || 'https://oa-sso.wsk.gmbh';
        const clientId = process.env.REACT_APP_OAUTH_CLIENT_ID || 'frontend-client';

        this.refreshPromise = (async () => {
            try {
                const response = await fetch(`${ssoUrl}/token`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({
                        grant_type: 'refresh_token',
                        refresh_token: refreshToken,
                        client_id: clientId,
                    }),
                });
                if (!response.ok) {
                    if (response.status >= 400 && response.status < 500) {
                        this.removeToken();
                        this.removeRefreshToken();
                    }
                    return false;
                }
                const data = await response.json();
                if (!data.access_token) {
                    return false;
                }
                this.setToken(data.access_token);
                if (data.refresh_token) {
                    this.setRefreshToken(data.refresh_token);
                }
                return true;
            } catch {
                return false;
            } finally {
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }

    /**
     * Check if a valid token exists
     */
    hasValidToken(): boolean {
        const token = this.getToken();
        if (!token) return false;

        try {
            const decoded = this.decodeToken(token);
            return !this.isTokenExpired(decoded);
        } catch {
            return false;
        }
    }

    /**
     * Decode the JWT token
     */
    decodeToken(token?: string): JwtPayload {
        const tokenToUse = token ?? this.getToken();
        if (!tokenToUse) {
            throw new Error('No token available');
        }
        return jwtDecode<JwtPayload>(tokenToUse);
    }

    /**
     * Check if token is expired
     */
    isTokenExpired(decoded?: JwtPayload): boolean {
        const payload = decoded ?? this.decodeToken();
        const now = Date.now() / 1000;
        return payload.exp < now;
    }

    /**
     * Get roles from token
     */
    getRoles(): string[] {
        try {
            const decoded = this.decodeToken();
            const roles = decoded.roles || [];

            // Handle both array and object formats
            if (Array.isArray(roles)) {
                return roles;
            }
            if (typeof roles === 'object') {
                return Object.values(roles);
            }
            return [];
        } catch {
            return [];
        }
    }

    /**
     * Get permissions from token
     */
    getPermissions(): string[] {
        try {
            const decoded = this.decodeToken();
            const permissions = decoded.permissions || [];

            // Handle both array and object formats
            if (Array.isArray(permissions)) {
                return permissions;
            }
            if (typeof permissions === 'object') {
                return Object.values(permissions);
            }
            return [];
        } catch {
            return [];
        }
    }

    /**
     * License-based feature check: a token without a `features` claim allows
     * everything (legacy tokens, platform roles); with the claim present the
     * feature must be listed explicitly. Missing/invalid token also returns
     * true — the auth guards handle that case.
     */
    hasFeature(name: string): boolean {
        try {
            const decoded = this.decodeToken();
            const features = decoded.features;

            if (features === undefined || features === null) {
                return true;
            }
            if (Array.isArray(features)) {
                return features.includes(name);
            }
            if (typeof features === 'object') {
                return Object.values(features).includes(name);
            }
            return true;
        } catch {
            return true;
        }
    }

    /**
     * Get combined roles and permissions
     */
    getAllPermissions(): string[] {
        return [...this.getPermissions(), ...this.getRoles()];
    }

    /**
     * Numerische User-ID aus dem JWT-`userId`-Claim (Issue #400).
     *
     * Das ist dieselbe ID, die der API-CreatedBySubscriber in `createdBy`-
     * Felder schreibt — im Gegensatz zu `identity.id` aus useGetIdentity()
     * synchron verfuegbar und ohne Netzwerk-Roundtrip zum SSO-userinfo-
     * Endpoint. Fuer "eigener Datensatz?"-Vergleiche immer diese Methode
     * verwenden, nie identity.id.
     */
    getUserId(): number | null {
        try {
            const decoded = this.decodeToken();
            return typeof decoded.userId === 'number' ? decoded.userId : null;
        } catch {
            return null;
        }
    }

    /**
     * Get company ID from token
     */
    getCompanyId(): number | null {
        try {
            const decoded = this.decodeToken();
            return decoded.companyId ?? null;
        } catch {
            return null;
        }
    }

    /**
     * Mandanten der eigenen Unternehmensgruppe (Claim `companyGroupMemberIds`).
     * Leer, wenn der Token keinen Claim trägt.
     */
    getCompanyGroupMemberIds(): number[] {
        try {
            const members = this.decodeToken().companyGroupMemberIds;
            return Array.isArray(members) ? members.filter((id): id is number => typeof id === 'number') : [];
        } catch {
            return [];
        }
    }

    /** Gehört der Mandant einer Unternehmensgruppe an (mehr als nur er selbst)? */
    hasCompanyGroup(): boolean {
        return this.getCompanyGroupMemberIds().length > 1;
    }

    // PKCE helpers
    getCodeVerifier(): string | null {
        return localStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
    }

    setCodeVerifier(verifier: string): void {
        localStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, verifier);
    }

    removeCodeVerifier(): void {
        localStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
    }

    // Login state helpers

    /**
     * Das gespeicherte Login-Ziel, sofern es auf diese Anwendung zeigt.
     *
     * Der Wert landet im AuthCallback in window.location.replace(). Ein Ziel
     * mit fuehrendem Doppel-Slash ist protokoll-relativ und wuerde den Browser
     * auf eine fremde Domain schicken, deshalb kommt hier nur ein Pfad zurueck,
     * der mit genau einem Slash beginnt.
     */
    getRedirectPath(): string | null {
        const path = localStorage.getItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN);

        if (path === null) {
            return null;
        }

        return /^\/(?!\/)/.test(path) ? path : null;
    }

    setRedirectPath(path: string): void {
        localStorage.setItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN, path);
    }

    removeRedirectPath(): void {
        localStorage.removeItem(STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
    }

    isUserLoggedOut(): boolean {
        return localStorage.getItem(STORAGE_KEYS.USER_LOGGED_OUT) === 'true';
    }

    setUserLoggedOut(value: boolean): void {
        if (value) {
            localStorage.setItem(STORAGE_KEYS.USER_LOGGED_OUT, 'true');
        } else {
            localStorage.removeItem(STORAGE_KEYS.USER_LOGGED_OUT);
        }
    }

    isAutoLoginInProgress(): boolean {
        return localStorage.getItem(STORAGE_KEYS.AUTO_LOGIN_IN_PROGRESS) === 'true';
    }

    setAutoLoginInProgress(value: boolean): void {
        if (value) {
            localStorage.setItem(STORAGE_KEYS.AUTO_LOGIN_IN_PROGRESS, 'true');
        } else {
            localStorage.removeItem(STORAGE_KEYS.AUTO_LOGIN_IN_PROGRESS);
        }
    }

    // Identity cache (sessionStorage)
    getCachedIdentity(): UserIdentity | null {
        try {
            const cached = sessionStorage.getItem(STORAGE_KEYS.IDENTITY);
            return cached ? JSON.parse(cached) : null;
        } catch {
            return null;
        }
    }

    setCachedIdentity(identity: UserIdentity): void {
        sessionStorage.setItem(STORAGE_KEYS.IDENTITY, JSON.stringify(identity));
    }

    clearCachedIdentity(): void {
        sessionStorage.removeItem(STORAGE_KEYS.IDENTITY);
    }

    /**
     * Clear all auth-related data
     */
    clearAll(): void {
        this.removeToken();
        this.removeRefreshToken();
        this.removeCodeVerifier();
        this.removeRedirectPath();
        this.clearCachedIdentity();
        sessionStorage.clear();
    }
}

// Export singleton instance
export const tokenService = new TokenService();
export default tokenService;
